/*
 * Sneakerwear motion layer.
 * - Scroll reveal on every section below the fold, adapted to each section's content
 * - Compact header once the sticky header is active (no layout shift)
 * - Magnetic hover on buttons (mouse / trackpad only)
 * - Count-up numbers when they scroll into view
 * The animated hero gradient lives in CSS (assets/sneakerwear.css).
 * Nothing runs when the visitor prefers reduced motion. Only transform and opacity animate.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches) return;

  const root = document.documentElement;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const supportsIO = 'IntersectionObserver' in window;
  root.classList.add('sw-motion');

  /* ---------- Scroll reveal: every section below the fold ----------
   * Each section's content enters with a staggered fade + rise. The effect adapts to what the
   * section holds: stat tiles scale in, the two promo cards slide in from each side, banner
   * images zoom out, and cards, brand tiles, reviews and FAQ rows rise one after the other.
   * Sections without recognisable items fade and rise as a whole.
   */
  const REVEAL_CLEANUP_MS = 1900;
  const MAX_STAGGER = 10;
  const ITEM_SELECTORS = [
    '.sw-stats__item',
    '[data-carousel-card]',
    'product-card',
    '.resource-list__item',
    'details.details',
    '.group-block',
  ];

  const revealObserver = supportsIO
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const section = entry.target;
            revealObserver.unobserve(section);
            section.classList.add('is-visible');
            // Hand everything back to the theme (hover effects, carousels) once the entrance is done.
            window.setTimeout(() => cleanUpReveal(section), REVEAL_CLEANUP_MS);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

  function cleanUpReveal(section) {
    section.classList.remove('sw-reveal', 'sw-reveal--whole', 'is-visible');
    section.querySelectorAll('.sw-item').forEach((item) => {
      item.classList.remove('sw-item');
      item.removeAttribute('data-sw-anim');
      item.style.removeProperty('--sw-i');
    });
  }

  function pickItems(section) {
    if (section.querySelector('.hero__content-wrapper')) {
      return Array.from(section.querySelectorAll('.hero__content-wrapper > *'));
    }
    for (const selector of ITEM_SELECTORS) {
      const items = Array.from(section.querySelectorAll(selector));
      if (items.length) return items;
    }
    return [];
  }

  function animationFor(section, index) {
    const id = section.id || '';
    if (id.endsWith('__stats')) return 'scale';
    if (id.endsWith('__promo_cards')) return index % 2 === 0 ? 'left' : 'right';
    return 'up';
  }

  function prepareSection(section) {
    const items = pickItems(section);
    if (!items.length) {
      section.classList.add('sw-reveal', 'sw-reveal--whole');
      return;
    }
    const heading = section.querySelector('h2, h3');
    const headingIsSeparate = heading && !items.some((item) => item.contains(heading));
    const sequence = headingIsSeparate ? [heading, ...items] : items;
    sequence.forEach((item, index) => {
      item.classList.add('sw-item');
      item.dataset.swAnim = item === heading ? 'up' : animationFor(section, index);
      item.style.setProperty('--sw-i', String(Math.min(index, MAX_STAGGER)));
    });
    section.classList.add('sw-reveal');
  }

  function setupReveal() {
    if (!revealObserver || designMode) return;
    const sections = Array.from(
      document.querySelectorAll('#MainContent > .shopify-section:not([data-sw-reveal])')
    );
    // Read every position first, then write, to avoid layout thrashing.
    const viewportBottom = window.innerHeight * 0.92;
    const belowFold = sections.filter((section) => section.getBoundingClientRect().top > viewportBottom);
    for (const section of sections) section.dataset.swReveal = 'done';
    for (const section of belowFold) {
      prepareSection(section);
      revealObserver.observe(section);
    }
  }

  /* ---------- Compact header ---------- */
  function setupHeader() {
    const header = document.getElementById('header-component');
    if (!header) return;
    const section = header.closest('.header-section') || header.parentElement;
    if (!section) return;

    // Keep the sticky wrapper at its full height so content never jumps when the header compacts.
    const lockHeight = () => {
      if (header.dataset.stickyState === 'active') return;
      section.classList.remove('sw-header-locked');
      const height = section.offsetHeight;
      if (!height) return;
      section.style.setProperty('--sw-header-h', `${height}px`);
      section.classList.add('sw-header-locked');
    };

    lockHeight();
    new MutationObserver(() => {
      if (!section.classList.contains('sw-header-locked')) lockHeight();
    }).observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });

    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(lockHeight, 150);
      },
      { passive: true }
    );
  }

  /* ---------- Magnetic buttons ---------- */
  function setupMagnetic() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const SELECTOR = '.button:not(.button-unstyled), .button-secondary, .button-custom';
    const STRENGTH = 0.3;
    const MAX_SHIFT = 10;
    const MAX_WIDTH = 360; // full-width buttons (add to cart) stay still
    const clamp = (value) => Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, value));

    let current = null;
    let rect = null;
    let frame = 0;
    let shiftX = 0;
    let shiftY = 0;

    const paint = () => {
      frame = 0;
      if (!current) return;
      current.style.setProperty('--sw-mx', `${shiftX.toFixed(1)}px`);
      current.style.setProperty('--sw-my', `${shiftY.toFixed(1)}px`);
    };
    const release = (element) => {
      element.classList.remove('sw-magnet-active');
      element.style.setProperty('--sw-mx', '0px');
      element.style.setProperty('--sw-my', '0px');
    };

    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
        if (!target || target === current) return;
        if (current) release(current);
        const box = target.getBoundingClientRect();
        if (box.width > MAX_WIDTH) {
          current = null;
          return;
        }
        current = target;
        rect = box;
        target.classList.add('sw-magnetic', 'sw-magnet-active');
      },
      { passive: true }
    );

    document.addEventListener(
      'pointermove',
      (event) => {
        if (!current || !rect) return;
        shiftX = clamp((event.clientX - (rect.left + rect.width / 2)) * STRENGTH);
        shiftY = clamp((event.clientY - (rect.top + rect.height / 2)) * STRENGTH);
        if (!frame) frame = window.requestAnimationFrame(paint);
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerout',
      (event) => {
        if (!current) return;
        const next = event.relatedTarget;
        if (next instanceof Node && current.contains(next)) return;
        release(current);
        current = null;
        rect = null;
      },
      { passive: true }
    );

    window.addEventListener(
      'scroll',
      () => {
        if (current) rect = current.getBoundingClientRect();
      },
      { passive: true, capture: true }
    );
  }

  /* ---------- Count-up numbers ---------- */
  const formatter = new Intl.NumberFormat('en-GB');
  const countObserver = supportsIO
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            countObserver.unobserve(entry.target);
            countUp(entry.target);
          }
        },
        { threshold: 0.6 }
      )
    : null;

  function countUp(element) {
    const target = Number.parseInt(element.dataset.swCount, 10);
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      element.textContent = formatter.format(Math.round(target * eased));
      if (progress < 1) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  function setupCounters() {
    if (!countObserver) return;
    document.querySelectorAll('[data-sw-count]:not([data-sw-count-ready])').forEach((element) => {
      element.dataset.swCountReady = 'true';
      const target = Number.parseInt(element.dataset.swCount, 10);
      if (!Number.isFinite(target) || target <= 0) return;
      element.textContent = '0';
      countObserver.observe(element);
    });
  }

  /* ---------- Reduced motion switched on mid-visit: stop everything ---------- */
  const stopAll = () => {
    root.classList.remove('sw-motion');
    revealObserver?.disconnect();
    countObserver?.disconnect();
    document.querySelectorAll('.sw-reveal').forEach((el) => cleanUpReveal(el));
    document.querySelectorAll('[data-sw-count]').forEach((el) => {
      el.textContent = formatter.format(Number.parseInt(el.dataset.swCount, 10) || 0);
    });
  };
  reducedMotion.addEventListener?.('change', (event) => {
    if (event.matches) stopAll();
  });

  const init = () => {
    setupReveal();
    setupCounters();
  };

  setupHeader();
  setupMagnetic();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // Theme editor: re-run on sections added or re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
